/** Customer wire / gallery type for 3D models (never `GLB` / `3d_modal`).
 *
 * Keep detection/pin behavior in lockstep with
 * `App\Support\ProductMedia\ProductMedia3dModel` (PHP).
 */
export const MEDIA_TYPE_3D_MODEL = '3d_model';

/**
 * @param {string | undefined | null} type
 * @returns {boolean}
 */
export function is3dModelType(type) {
	return type === MEDIA_TYPE_3D_MODEL;
}

/**
 * @param {Record<string, unknown>} rawImage
 * @returns {string}
 */
function readMediaType(rawImage) {
	const mediaType = rawImage.media_type ?? rawImage.mediaType;
	if (typeof mediaType === 'string' && mediaType !== '') {
		return mediaType;
	}

	const type = rawImage.type;
	if (typeof type === 'string' && type === MEDIA_TYPE_3D_MODEL) {
		return type;
	}

	return '';
}

/**
 * @param {Record<string, unknown>} rawImage
 * @param {'main'|'preview'|'thumb'} sizeKey
 * @returns {string}
 */
function readNestedImageUrl(rawImage, sizeKey) {
	const size = rawImage[sizeKey];
	if (size !== null && typeof size === 'object' && typeof size.url === 'string') {
		return size.url;
	}

	if (typeof size === 'string') {
		return size;
	}

	return '';
}

/**
 * Single detection owner: media_type / mediaType (or already-normalized type) === 3d_model.
 *
 * @param {Record<string, unknown>} rawImage
 * @returns {boolean}
 */
export function is3dModelRawImage(rawImage) {
	return readMediaType(rawImage) === MEDIA_TYPE_3D_MODEL;
}

/**
 * @param {Record<string, unknown>} rawImage
 * @returns {string}
 */
export function resolveModelUrlFromRawImage(rawImage) {
	const explicitModelUrl = rawImage.model_url ?? rawImage.modelUrl;
	if (typeof explicitModelUrl === 'string' && explicitModelUrl !== '') {
		return explicitModelUrl;
	}

	return readNestedImageUrl(rawImage, 'main');
}

/**
 * Pins the product's 3D model to slot 2 (index 1) when a featured raster exists.
 * Common upload enforces one customer `3d_model` per product.
 *
 * @template {{ type?: string, media_type?: string, mediaType?: string }} T
 * @param {T[]} productImages
 * @returns {T[]}
 */
export function pinModel3dToSlot2(productImages) {
	const modelIndex = productImages.findIndex(is3dModelRawImage);

	if (modelIndex === -1) {
		return productImages;
	}

	const model = productImages[modelIndex];
	const others = productImages.filter((_, index) => index !== modelIndex);

	if (others.length === 0) {
		return [model];
	}

	const [featured, ...rest] = others;

	return [featured, model, ...rest];
}

/**
 * @template {Record<string, unknown>} T
 * @param {T[]} rawImages
 * @returns {T | null}
 */
export function firstNon3dRawImage(rawImages) {
	for (const image of rawImages) {
		if (!is3dModelRawImage(image)) {
			return image;
		}
	}

	return null;
}
